// Derive the whole Sampling History screen from the job-level rows returned by
// GET /api/v1/sampling-history (dbo.vw_RecurrenceJobSampling). The view is one
// row per past scheduled job (now including the real RecurranceType frequency);
// the calendar pivot (site × month), the per-site point × month drill, the month
// columns and the per-COC frequency (real RecurranceType, cadence only as a
// fallback) are all COMPUTED here so a single call feeds the screen. Static-data
// shapes are reused (samplingHistoryData.ts types) so the page renders identically.
import { fmtLocalDate } from '../../utils/datetime';
import type {
  HistMonth, HistLedgerRow, HistMatrixRow, HistSiteGridEntry, HistMetaEntry, CellState,
} from './samplingHistoryData';

/** One row as the API serialises SamplingHistoryRow (camelCase). */
export interface SamplingHistoryApiRow {
  cocId: string | null;
  site: string | null;
  client: string | null;
  project: string | null;
  matrix: string | null;
  jobId: string | null;
  points: string | null;
  scheduled: string | null;
  collected: string | null;
  totalSamples: number;
  sampled: number;
  notSampled: number;
  status: string | null;
  /** RecurranceType (Daily/Weekly/Monthly/Yearly) — the real Frequency; null when
   *  the job has no scheduler entry (then frequency falls back to date cadence). */
  recurrence: string | null;
  /** Per-point sampled/total for this job as JSON [{p,s,t}] (p = site/point name,
   *  s = sampled, t = total). Drives the accurate point×month drill; null when no
   *  sample maps to a named site (then the drill falls back to the job status). */
  pointBreakdown: string | null;
}

/** One point's sampled/total within a job (parsed from PointBreakdown). */
interface PointStat { p: string; s: number; t: number; }

/** Parse the SP's PointBreakdown JSON into per-point sampled/total stats. */
function parseBreakdown(json: string | null): PointStat[] {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr)
      ? arr.filter((x): x is PointStat => x && typeof x.p === 'string'
          && typeof x.s === 'number' && typeof x.t === 'number')
      : [];
  } catch { return []; }
}

export interface DerivedSamplingHistory {
  months: HistMonth[];
  ledger: HistLedgerRow[];
  matrix: HistMatrixRow[];
  sitegrid: Record<string, HistSiteGridEntry>;
  meta: Record<string, HistMetaEntry>;
}

const MON = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Parse a date-only / ISO string into {y, m, d} (local, no timezone shift). */
function ymd(value: string | null): { y: number; m: number; d: number } | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (m) return { y: +m[1]!, m: +m[2]!, d: +m[3]! };
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
}

/** Month key "YYYY-M" (no leading zero — matches the prototype's 2026-4). */
const monthKey = (p: { y: number; m: number }) => `${p.y}-${p.m}`;
const monthLabel = (p: { y: number; m: number }) => `${MON[p.m]} '${String(p.y).slice(2)}`;

/** Whole-day difference collected − scheduled (for the "+Nd" late tag). */
function dayDiff(a: { y: number; m: number; d: number }, b: { y: number; m: number; d: number }): number {
  const ms = Date.UTC(a.y, a.m - 1, a.d) - Date.UTC(b.y, b.m - 1, b.d);
  return Math.round(ms / 86_400_000);
}

/** Median of a numeric list. */
function median(nums: number[]): number {
  if (nums.length === 0) return 0;
  const s = [...nums].sort((x, y) => x - y);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

/** Frequency key from the median gap (days) between a COC's scheduled dates. */
function freqOfGap(gap: number): string {
  if (gap <= 0) return 'm';       // single event → assume monthly
  if (gap <= 10) return 'w';      // weekly
  if (gap <= 20) return 'bw';     // biweekly / semi-monthly band
  if (gap <= 45) return 'm';      // monthly
  if (gap <= 120) return 'q';     // quarterly
  return 'o';
}
const FREQ_LABELS: Record<string, string> = {
  m: 'Monthly', w: 'Weekly', bw: 'Biweekly', sm: 'Semi-monthly', q: 'Quarterly', o: 'Other',
};

/** The REAL frequency from RecurranceType → {pill key, display label}. Daily and
 *  Yearly have no dedicated pill, so they use the neutral 'o' pill but keep their
 *  own label. Returns null for an unknown/blank value (caller falls back to cadence). */
function freqFromRecurrence(rt: string | null | undefined): { fk: string; label: string } | null {
  switch ((rt ?? '').trim().toLowerCase()) {
    case 'monthly': return { fk: 'm', label: 'Monthly' };
    case 'weekly':  return { fk: 'w', label: 'Weekly' };
    case 'daily':   return { fk: 'o', label: 'Daily' };
    case 'yearly':  return { fk: 'o', label: 'Yearly' };
    default:        return null;
  }
}

/** Cell state for a set of jobs in one site-month. */
function cellFor(statuses: string[]): CellState {
  if (statuses.length === 0) return 'na';
  const allSampled = statuses.every((s) => s === 'Sampled');
  if (allSampled) return 'ok';
  const allMissed = statuses.every((s) => s === 'Not sampled');
  if (allMissed) return 'miss';
  return 'part';
}

export function deriveSamplingHistory(apiRows: SamplingHistoryApiRow[]): DerivedSamplingHistory {
  // ── Months present in the data (chronological) ──
  const monthSet = new Map<string, { y: number; m: number }>();
  for (const r of apiRows) {
    const p = ymd(r.scheduled);
    if (p) monthSet.set(monthKey(p), { y: p.y, m: p.m });
  }
  const monthsSorted = [...monthSet.values()].sort((a, b) => a.y - b.y || a.m - b.m);
  const months: HistMonth[] = monthsSorted.map((p) => ({ mk: monthKey(p), label: monthLabel(p) }));

  // ── Ledger rows (job level) ──
  const ledger: HistLedgerRow[] = apiRows.map((r) => {
    const sp = ymd(r.scheduled);
    const cp = ymd(r.collected);
    const late = sp && cp ? dayDiff(cp, sp) : 0;
    return {
      coc: r.cocId ?? '',
      site: r.site ?? '',
      client: r.client ?? '',
      project: r.project ?? '',
      fk: 'm', // replaced below once the per-COC cadence is known
      frequency: 'Monthly',
      jobId: r.jobId ?? '',
      points: r.points ?? '',
      scheduled: r.scheduled ? (fmtLocalDate(r.scheduled) || '') : '',
      collected: r.collected ? (fmtLocalDate(r.collected) || '') : '',
      late: late > 0 ? `+${late}d` : '',
      sampledTotal: `${r.sampled}/${r.totalSamples}`,
      status: r.status ?? '',
    };
  });

  // ── Per-COC grouping (matrix, frequency, sitegrid, meta) ──
  const byCoc = new Map<string, SamplingHistoryApiRow[]>();
  for (const r of apiRows) {
    const key = r.cocId ?? '';
    (byCoc.get(key) ?? byCoc.set(key, []).get(key)!).push(r);
  }

  const matrix: HistMatrixRow[] = [];
  const sitegrid: Record<string, HistSiteGridEntry> = {};
  const meta: Record<string, HistMetaEntry> = {};
  const freqByCoc = new Map<string, { fk: string; label: string }>();

  for (const [coc, rows] of byCoc) {
    const site = rows[0]?.site ?? coc;

    // Frequency: prefer the REAL RecurranceType from the SP; only when no job in
    // this COC carries one do we fall back to inferring it from the scheduled-date
    // cadence (median gap between consecutive scheduled dates).
    const realFreq = freqFromRecurrence(rows.find((r) => r.recurrence)?.recurrence ?? null);
    let freq: { fk: string; label: string };
    if (realFreq) {
      freq = realFreq;
    } else {
      const days = rows.map((r) => ymd(r.scheduled)).filter(Boolean)
        .map((p) => Date.UTC(p!.y, p!.m - 1, p!.d)).sort((a, b) => a - b);
      const gaps: number[] = [];
      for (let i = 1; i < days.length; i++) gaps.push(Math.round((days[i]! - days[i - 1]!) / 86_400_000));
      const fk = freqOfGap(median(gaps));
      freq = { fk, label: FREQ_LABELS[fk] ?? fk };
    }
    freqByCoc.set(coc, freq);

    // Calendar cells: this COC's jobs per month → one CellState.
    const cells: Record<string, CellState> = {};
    for (const mo of months) {
      const st = rows.filter((r) => { const p = ymd(r.scheduled); return p && monthKey(p) === mo.mk; })
        .map((r) => r.status ?? '');
      const c = cellFor(st);
      if (c !== 'na') cells[mo.mk] = c;
    }
    matrix.push({ site, coc, client: rows[0]?.client ?? '', cells });

    // Point × month drill from the REAL per-point split (PointBreakdown JSON per
    // job): a point's cell aggregates that point's sampled/total across the job(s)
    // scheduled that month → all sampled 'ok', none 'miss', mixed 'part'. Only when
    // a job carries no breakdown does it fall back to the old job-status guess for
    // its listed points.
    const bdByRow = new Map<SamplingHistoryApiRow, PointStat[]>();
    const pointSet = new Set<string>();
    for (const r of rows) {
      const bd = parseBreakdown(r.pointBreakdown);
      bdByRow.set(r, bd);
      if (bd.length) for (const x of bd) pointSet.add(x.p);
      else for (const p of (r.points ?? '').split(',').map((x) => x.trim())) if (p) pointSet.add(p);
    }
    const pcells: Record<string, Record<string, CellState>> = {};
    for (const p of pointSet) {
      const row: Record<string, CellState> = {};
      for (const mo of months) {
        const jobs = rows.filter((r) => { const q = ymd(r.scheduled); return q && monthKey(q) === mo.mk; });
        if (jobs.length === 0) continue;
        let s = 0, t = 0;
        const fallbackStatuses: string[] = [];
        for (const r of jobs) {
          const bd = bdByRow.get(r) ?? [];
          if (bd.length) {
            const st = bd.find((x) => x.p === p);
            if (st) { s += st.s; t += st.t; }
          } else if ((r.points ?? '').split(',').map((x) => x.trim()).includes(p)) {
            fallbackStatuses.push(r.status ?? '');
          }
        }
        if (t > 0) row[mo.mk] = s === 0 ? 'miss' : s === t ? 'ok' : 'part';
        else if (fallbackStatuses.length) row[mo.mk] = fallbackStatuses.every((x) => x === 'Not sampled') ? 'miss' : 'ok';
        // else 'na' (point not sampled in any of this month's jobs)
      }
      pcells[p] = row;
    }
    sitegrid[site] = { points: [...pointSet], cells: pcells };
    meta[site] = {
      client: rows[0]?.client ?? '',
      coc,
      project: rows[0]?.project ?? '',
      fk: freq.fk,
      freq: freq.label,
    };
  }

  // Stamp the per-COC frequency onto each ledger row.
  for (const row of ledger) {
    const freq = freqByCoc.get(row.coc) ?? { fk: 'm', label: 'Monthly' };
    row.fk = freq.fk;
    row.frequency = freq.label;
  }

  // Match the view's ORDER BY COC_ID (matrix follows the COC order in the data).
  matrix.sort((a, b) => a.coc.localeCompare(b.coc, undefined, { numeric: true }));

  return { months, ledger, matrix, sitegrid, meta };
}
